/* RESPONSIBLE TEAM: team-ai-insights */
import PALETTE from '@intercom/pulse/lib/palette';
import type IntlService from 'ember-intl/services/intl';
import {
  insightsMetrics,
  type ChartSeriesDefinition,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { type SerializedChartSeries } from 'embercom/components/reporting/in-context/static-report';
import type { Chart, ColorString } from 'highcharts';

type PaletteColor = keyof typeof PALETTE;
// these colors are copies from apps/teammate-app/src/index.css
// we need to use the values directly here so we can pass them to Highcharts
const colorForNoMetricValue: ColorString = 'var(--base-backdrop)';
const palette: Record<string, readonly PaletteColor[]> = {
  blue: [
    'vis-blue-20',
    'vis-blue-30',
    'vis-blue-40',
    'vis-blue-50',
    'vis-blue-60',
    'vis-blue-70',
    'vis-blue-80',
  ],
  salmon: [
    'vis-salmon-20',
    'vis-salmon-30',
    'vis-salmon-40',
    'vis-salmon-50',
    'vis-salmon-60',
    'vis-salmon-70',
    'vis-salmon-80',
  ],
  slateblue: [
    'vis-slateblue-20',
    'vis-slateblue-30',
    'vis-slateblue-40',
    'vis-slateblue-50',
    'vis-slateblue-60',
    'vis-slateblue-70',
    'vis-slateblue-80',
  ],
  cyan: [
    'vis-cyan-20',
    'vis-cyan-30',
    'vis-cyan-40',
    'vis-cyan-50',
    'vis-cyan-60',
    'vis-cyan-70',
    'vis-cyan-80',
  ],
  violet: [
    'vis-violet-20',
    'vis-violet-30',
    'vis-violet-40',
    'vis-violet-50',
    'vis-violet-60',
    'vis-violet-70',
    'vis-violet-80',
  ],
  mint: [
    'vis-mint-20',
    'vis-mint-30',
    'vis-mint-40',
    'vis-mint-50',
    'vis-mint-60',
    'vis-mint-70',
    'vis-mint-80',
  ],
  pink: [
    'vis-pink-20',
    'vis-pink-30',
    'vis-pink-40',
    'vis-pink-50',
    'vis-pink-60',
    'vis-pink-70',
    'vis-pink-80',
  ],
};

// This should match the separator used in the backend: TOPIC_SUBTOPIC_SEPARATOR
const topicSubtopicSeparator = '=>';

function topicPrefix(topicName: string) {
  return `${topicName}${topicSubtopicSeparator}`;
}

function buildStops(palette: readonly PaletteColor[]): Array<[number, PaletteColor]> {
  return palette.map((color, index) => [index / (palette.length - 1), color]);
}

type MetricConfig = {
  palette: readonly PaletteColor[];
  colorAxis: { stops: Array<[number, PaletteColor]> };
  seriesConfig: SerializedChartSeries;
  metricPickerItemClass: string;
  tooltipMetricColorClass: string;
  label: string;
  longLabel?: string;
  tooltip?: string;
};

type MetricId = ChartSeriesDefinition['metric_id'];

function metricConfigs(intl: IntlService): Record<MetricId, MetricConfig> {
  let metrics = insightsMetrics(intl);
  return {
    [metrics.aiCsatScore.metric_id]: {
      palette: palette.violet,
      colorAxis: {
        stops: buildStops(palette.violet),
      },
      metricPickerItemClass: 'bg-vis-violet-50',
      tooltipMetricColorClass: 'reporting__text-vis-violet-50',
      seriesConfig: {
        metric_id: metrics.aiCsatScore.metric_id,
        color: PALETTE['vis-violet-50'],
      },
      label: metrics.aiCsatScore.label,
      longLabel: intl.t(
        'components.reporting.automation.fin-insights.analyse.topics-explorer.metric-labels.cx-score',
      ),
      tooltip: metrics.aiCsatScore.tooltip,
    },
    [metrics.involvementRate.metric_id]: {
      palette: palette.blue,
      colorAxis: {
        stops: buildStops(palette.blue),
      },
      metricPickerItemClass: 'bg-vis-blue-50',
      tooltipMetricColorClass: 'reporting__text-vis-blue-50',
      seriesConfig: {
        metric_id: metrics.involvementRate.metric_id,
        color: PALETTE['vis-blue-50'],
      },
      label: metrics.involvementRate.label,
      tooltip: metrics.involvementRate.tooltip,
    },
    [metrics.automationRate.metric_id]: {
      palette: palette.cyan,
      colorAxis: {
        stops: buildStops(palette.cyan),
      },
      metricPickerItemClass: 'bg-vis-cyan-50',
      tooltipMetricColorClass: 'reporting__text-vis-cyan-50',
      seriesConfig: {
        metric_id: metrics.automationRate.metric_id,
        color: PALETTE['vis-cyan-50'],
      },
      label: metrics.automationRate.label,
      tooltip: metrics.automationRate.tooltip,
    },
    [metrics.resolutionRate.metric_id]: {
      palette: palette.mint,
      colorAxis: {
        stops: buildStops(palette.mint),
      },
      metricPickerItemClass: 'bg-vis-mint-50',
      tooltipMetricColorClass: 'reporting__text-vis-mint-50',
      seriesConfig: {
        metric_id: metrics.resolutionRate.metric_id,
        color: PALETTE['vis-mint-50'],
      },
      label: metrics.resolutionRate.label,
      tooltip: metrics.resolutionRate.tooltip,
    },
    [metrics.handlingTimeMedian.metric_id]: {
      palette: palette.salmon,
      colorAxis: {
        stops: buildStops([...palette.salmon].reverse()),
      },
      metricPickerItemClass: 'bg-vis-salmon-50',
      tooltipMetricColorClass: 'reporting__text-vis-salmon-50',
      seriesConfig: {
        metric_id: metrics.handlingTimeMedian.metric_id,
        aggregation: metrics.handlingTimeMedian.aggregation,
        color: PALETTE['vis-salmon-50'],
        filters: {
          type: 'exists',
          data: {
            property: 'first_reply_teammate_id',
            attribute: 'conversation.first_reply_teammate_id',
          },
        },
      },
      label: metrics.handlingTimeMedian.label,
      tooltip: metrics.handlingTimeMedian.tooltip,
    },
    [metrics.firstResponseTime.metric_id]: {
      palette: palette.pink,
      colorAxis: {
        stops: buildStops([...palette.pink].reverse()),
      },
      metricPickerItemClass: 'bg-vis-pink-50',
      tooltipMetricColorClass: 'reporting__text-vis-pink-50',
      seriesConfig: {
        metric_id: metrics.firstResponseTime.metric_id,
        aggregation: metrics.firstResponseTime.aggregation,
        color: PALETTE['vis-pink-50'],
      },
      label: metrics.firstResponseTime.label,
      tooltip: metrics.firstResponseTime.tooltip,
    },
  };
}

function addTopicFilter(seriesConfig: SerializedChartSeries, topicId: string) {
  let topicFilter = {
    type: 'category',
    data: {
      attribute: 'conversation_custom_fields#ai_insights_topic',
      property: 'conversation_custom_fields#ai_insights_topic',
      values: [topicId],
    },
  };

  return {
    ...seriesConfig,
    filters: {
      type: 'and',
      filters: [topicFilter, seriesConfig.filters].compact(),
    },
  };
}

function layoutColorAxisLabels(chart: Chart) {
  /* eslint-disable @intercom/intercom/no-bare-strings */
  // The labels are positioned just under the color axis, left aligned
  // move them up and the first one to the left, the last one to the right
  // and move the whole legend to the right to give space to the label now on the left
  let colorAxis = chart.axes.find((axis) => axis.coll === 'colorAxis');
  let ticks = Object.values(colorAxis!.ticks);
  let firstTick = ticks.find((tick) => tick.isFirst);
  let lastTick = ticks.find((tick) => tick.isLast);
  let el1 = firstTick!.label?.element as SVGTextElement;
  let el2 = lastTick!.label?.element as SVGTextElement;

  // move the first label left by it's width plus a gap
  let x = el1.getBBox().width + 10;
  el1.style.transform = `translate(${-x}px, -22px)`;
  // When the range is all the same value, the last tick is the same as the first one
  // so we don't need to move the last label
  if (firstTick !== lastTick) {
    el2.style.transform = `translate(12px, -22px)`;
  }
  // move the legend the width of the first label plus a gap from the edge
  // read the current transform matrix, to preserve the y position
  let legendGroup = chart.legend.group.element as SVGGElement;
  let transformMatrix = legendGroup.transform.baseVal.consolidate()!.matrix;
  legendGroup.style.transform = `translate(${x + 8}px, ${transformMatrix.f}px)`;
  /* eslint-enable @intercom/intercom/no-bare-strings */
}

// Usage: getComputedCssVar('--neutral-fill')
function getComputedCssVar(varName: string) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

export {
  colorForNoMetricValue,
  palette,
  buildStops,
  metricConfigs,
  type MetricId,
  addTopicFilter,
  getComputedCssVar,
  topicSubtopicSeparator,
  topicPrefix,
  layoutColorAxisLabels,
};
